// DayNotesModal — a general note for a work day, stored on that day's time_entries
// row (notes column) so it flows into the time export's Notes. Upserts the row:
// updates an existing entry, or creates a notes-only one (clock_in NULL — needs
// migration 014; otherwise set a Start Time first).
import React, { useState } from 'react';
import { supabase } from './supabaseClient.js';
import { useAuth } from './AuthContext.js';

const friendlyError = (e) => {
  const msg = e?.message || String(e);
  if (e?.code === '23502' || /clock_in/i.test(msg)) {
    return 'Apply migration 014 in Supabase to add notes without a start time — or set a Start Time first.';
  }
  if (/time_entr/i.test(msg) || e?.code === '42P01') {
    return 'Time tracking isn’t set up yet — apply migration 013 in Supabase, then try again.';
  }
  return msg;
};

const DayNotesModal = ({ route, dayNumber, workDate, existingEntry, onClose, onSaved }) => {
  const { user } = useAuth();
  const existing = existingEntry || null;
  const [notes, setNotes] = useState(existing?.notes || '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const handleSave = async () => {
    setError(null);
    setSaving(true);
    let e;
    if (existing) {
      ({ error: e } = await supabase.from('time_entries').update({ notes: notes.trim() || null }).eq('id', existing.id));
    } else {
      ({ error: e } = await supabase.from('time_entries').insert([{
        employee_id: route.applicator_id,
        work_date: workDate,
        route_id: route.id,
        notes: notes.trim() || null,
        created_by: user?.id || null,
      }]));
    }
    setSaving(false);
    if (e) { setError(friendlyError(e)); return; }
    onSaved();
  };

  const inputClass =
    'w-full px-3 py-1.5 bg-gray-900 border border-gray-500 rounded-md text-white text-sm focus:outline-none focus:ring-2 focus:ring-green-600';

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center px-4 pt-12 overflow-y-auto">
      <div className="fixed inset-0 bg-gray-900/80" onClick={onClose} />
      <div className="relative bg-gray-700 rounded-lg shadow-xl max-w-md w-full p-6 mb-12">
        <div className="flex items-center justify-between mb-1">
          <h3 className="text-lg font-semibold text-white">Day Notes — Day {dayNumber}</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-white text-xl">&times;</button>
        </div>
        <p className="text-xs text-gray-400 mb-4">{workDate} · shows in the time export's Notes for this day.</p>

        {error && (
          <div className="mb-4 px-3 py-2 bg-red-500/20 border border-red-500 rounded text-sm text-red-200">{error}</div>
        )}

        <textarea
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          rows={4}
          autoFocus
          placeholder="General notes for the day…"
          className={inputClass}
        />

        <div className="flex items-center justify-end pt-4 mt-3 border-t border-gray-500/30 gap-3">
          <button type="button" onClick={onClose} className="px-4 py-2 text-sm text-gray-300 hover:text-white transition-colors">Cancel</button>
          <button
            type="button"
            onClick={handleSave}
            disabled={saving}
            className="px-4 py-2 text-sm font-medium bg-green-700 text-white rounded-md hover:bg-green-600 disabled:opacity-50 transition-colors"
          >
            {saving ? 'Saving…' : 'Save Notes'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default DayNotesModal;
